import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Truck, Route as RouteIcon, Package, User, RefreshCw, Search, CheckCircle2, PenLine, Download } from "lucide-react";
import Gated from "@/components/Gated";
import EmptyState from "@/components/EmptyState";
import SignaturePad from "@/components/SignaturePad";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API_BASE } from "@/api/client";

/**
 * Drop Routes — Executive Workflow (Phase 2 · Feb 2026)
 *
 * Flow: Pick a Route → see parcels tagged to that Route → multi-select
 * → Assign to a Runner (active Delivery Staff for the tenant).
 *
 * Company Executive is expected here; Admins can also use it.
 */
export default function DropWorkflowPage() {
  const { user } = useAuth();
  if (user?.role === "delivery_staff") return <RunnerView />;
  return <ExecutiveView />;
}

function ExecutiveView() {
  const [routes, setRoutes] = useState([]);
  const [runners, setRunners] = useState([]);
  const [routeId, setRouteId] = useState("");
  const [parcels, setParcels] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [onlyUnassigned, setOnlyUnassigned] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [runnerId, setRunnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadRoutes  = async () => { try { const { data } = await api.get("/routes"); setRoutes(data || []); } catch { setRoutes([]); } };
  const loadRunners = async () => { try { const { data } = await api.get("/delivery-staff"); setRunners((data || []).filter(s => s.active !== false)); } catch { setRunners([]); } };

  const loadParcels = async () => {
    if (!routeId) { setParcels([]); return; }
    setLoading(true);
    try {
      const params = { route_id: routeId, limit: 500 };
      if (onlyUnassigned) params.assigned = false;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/drop-workflow/parcels", { params });
      setParcels(data || []);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load parcels"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRoutes(); loadRunners(); }, []);
  useEffect(() => { loadParcels(); /* eslint-disable-next-line */ }, [routeId, onlyUnassigned]);

  const routeMeta = useMemo(() => routes.find(r => r.id === routeId) || null, [routes, routeId]);
  const allChecked = parcels.length > 0 && selected.size === parcels.length;
  const someChecked = selected.size > 0 && !allChecked;

  const toggle = (id) => {
    setSelected(s => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(parcels.map(p => p.id)));
  };

  const openAssign = () => {
    if (selected.size === 0) return toast.error("Select at least one parcel");
    setRunnerId(""); setAssignOpen(true);
  };
  const confirmAssign = async () => {
    if (!runnerId) return toast.error("Pick a runner");
    setBusy(true);
    try {
      const { data } = await api.post("/drop-workflow/assign-runner", {
        parcel_ids: Array.from(selected),
        delivery_staff_id: runnerId,
      });
      toast.success(`${data.assigned} parcel(s) assigned to ${data.runner}`);
      setAssignOpen(false);
      loadParcels();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  return (
    <Gated feature="drop_workflow">
      <div className="p-4 sm:p-8 space-y-6 max-w-6xl mx-auto" data-testid="drop-routes-page">
        <header className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline flex items-center gap-2"><Truck size={12}/> Executive workflow</div>
            <h1 className="text-3xl sm:text-4xl font-display mt-1">Drop Routes.</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Pick a route, review the day's parcels tagged to it, and dispatch them to a runner in a single click.
            </p>
          </div>
          <Button variant="outline" onClick={loadParcels} disabled={!routeId || loading} data-testid="drop-refresh"
                  className="rounded-md"><RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
          {/* Phase 3 · Executive can export delivery logs */}
          <a href={`${API_BASE}/drop-logs/export?format=xlsx`}
             target="_blank" rel="noopener noreferrer" data-testid="drop-export-xlsx"
             className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-xs font-semibold hover:bg-muted transition">
            <Download size={13} /> Export XLSX
          </a>
          <a href={`${API_BASE}/drop-logs/export?format=pdf`}
             target="_blank" rel="noopener noreferrer" data-testid="drop-export-pdf"
             className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-xs font-semibold hover:bg-muted transition">
            <Download size={13} /> Export PDF
          </a>
        </header>

        {/* Route selector */}
        <section className="border border-border rounded-md bg-card p-4" data-testid="drop-route-picker">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block md:col-span-1">
              <span className="overline flex items-center gap-1.5"><RouteIcon size={11}/> Delivery Route</span>
              <select data-testid="drop-route-select" value={routeId} onChange={(e) => setRouteId(e.target.value)}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                <option value="">— pick an active route —</option>
                {routes.map(r => <option key={r.id} value={r.id}>{r.name} · {r.code}</option>)}
              </select>
            </label>
            <label className="block md:col-span-1">
              <span className="overline">Search within route</span>
              <div className="relative mt-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="drop-parcel-search" value={q} onChange={e=>setQ(e.target.value)}
                       onKeyDown={e=>{ if (e.key === "Enter") loadParcels(); }}
                       className="pl-8 rounded-md" placeholder="Tracking / recipient / sender…" />
              </div>
            </label>
            <label className="block md:col-span-1 flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="drop-only-unassigned"
                       checked={onlyUnassigned} onChange={e => setOnlyUnassigned(e.target.checked)}
                       className="h-4 w-4 accent-primary" />
                <span>Only unassigned</span>
              </label>
            </label>
          </div>
          {routeMeta && (
            <div className="mt-3 text-[11px] text-muted-foreground">
              <b>{routeMeta.name}</b> · <span className="mono">{routeMeta.code}</span> · <span className="uppercase tracking-widest">{routeMeta.status}</span>
            </div>
          )}
        </section>

        {/* Parcel list */}
        <section className="border border-border rounded-md bg-card" data-testid="drop-parcels">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <div className="overline flex items-center gap-1.5"><Package size={11}/> Parcels for route · {parcels.length}</div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground" data-testid="drop-selected-count">{selected.size} selected</span>
              <Button size="sm" onClick={openAssign} disabled={selected.size === 0} data-testid="drop-assign-btn"
                      className="rounded-md bg-primary text-primary-foreground shadow-brut-orange h-8">
                <User size={12} className="mr-1.5" /> Assign to Runner
              </Button>
            </div>
          </div>

          {!routeId ? (
            <EmptyState icon={RouteIcon} title="Pick a route to begin"
                        subtitle="Only Active routes appear in the picker above."
                        testid="drop-no-route" />
          ) : parcels.length === 0 ? (
            <EmptyState icon={Package} title="No parcels for this route"
                        subtitle={onlyUnassigned ? "All parcels on this route already have a runner. Untick 'Only unassigned' to see the full list." : "Nothing tagged to this route yet."}
                        testid="drop-empty-list" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="drop-parcels-table">
                <thead className="bg-muted text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="p-2 w-10 text-left">
                      <input type="checkbox" data-testid="drop-select-all"
                             checked={allChecked}
                             ref={el => { if (el) el.indeterminate = someChecked; }}
                             onChange={toggleAll}
                             className="h-4 w-4 accent-primary cursor-pointer" />
                    </th>
                    <th className="p-2 text-left">Tracking</th>
                    <th className="p-2 text-left">Recipient</th>
                    <th className="p-2 text-left">Sender</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Runner</th>
                    <th className="p-2 text-right mono text-[10px]">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {parcels.map(p => (
                    <tr key={p.id} className={`border-t border-border ${selected.has(p.id) ? "bg-red-50/40" : "hover:bg-muted/30"}`}
                        data-testid={`drop-parcel-${p.tracking_number}`}>
                      <td className="p-2">
                        <input type="checkbox" data-testid={`drop-check-${p.id}`}
                               checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                               className="h-4 w-4 accent-primary cursor-pointer" />
                      </td>
                      <td className="p-2 mono text-xs font-bold">{p.tracking_number}</td>
                      <td className="p-2">
                        <div>{p.recipient_name || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">{p.recipient_department || ""}</div>
                      </td>
                      <td className="p-2 text-[12px] text-muted-foreground">{p.sender_name || "—"}</td>
                      <td className="p-2 text-[10px] uppercase tracking-widest">{(p.status || "").replace(/_/g," ")}</td>
                      <td className="p-2 text-[11px]">
                        {p.assigned_staff_name ? (
                          <span className="text-emerald-700 font-semibold">{p.assigned_staff_name}</span>
                        ) : (
                          <span className="text-muted-foreground">— unassigned —</span>
                        )}
                      </td>
                      <td className="p-2 text-right mono text-[10px] text-muted-foreground">{(p.created_at || "").slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Assign dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="rounded-md max-w-md" data-testid="drop-assign-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl">Assign runner</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Assigning <b className="text-foreground">{selected.size}</b> parcel(s) on route <b className="text-foreground">{routeMeta?.name}</b> to a delivery staff member.
              </div>
              <label className="block">
                <span className="overline">Runner *</span>
                <select data-testid="drop-runner-select" value={runnerId} onChange={e => setRunnerId(e.target.value)}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                  <option value="">— pick an active runner —</option>
                  {runners.map(s => <option key={s.id} value={s.id}>{s.name}{s.phone ? ` · ${s.phone}` : ""}</option>)}
                </select>
                {runners.length === 0 && (
                  <div className="text-[10px] text-muted-foreground mt-1">
                    No active delivery staff. Ask a Company Admin to add them under <b>Master → Delivery Staff</b>.
                  </div>
                )}
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAssignOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={confirmAssign} disabled={busy || !runnerId} data-testid="drop-assign-confirm"
                        className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
                  {busy ? "Assigning…" : <>Assign <CheckCircle2 size={13} className="ml-1.5" /></>}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Gated>
  );
}

/* ============ Phase 3 · Feb 2026 · Runner View (delivery_staff role) ============ */
function RunnerView() {
  const [parcels, setParcels] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [dlvOpen, setDlvOpen] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const [signatureUrl, setSignatureUrl] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/drop-workflow/my-assigned");
      setParcels(data || []);
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = parcels.length > 0 && selected.size === parcels.length;
  const someChecked = selected.size > 0 && !allChecked;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(parcels.map(p => p.id)));

  const openDlv = () => {
    if (selected.size === 0) return toast.error("Pick at least one parcel");
    setSignedBy(""); setSignatureUrl(""); setDropAddress(""); setRemarks("");
    setDlvOpen(true);
  };

  const confirmDeliver = async () => {
    if (!signedBy.trim()) return toast.error("Enter the recipient's name");
    if (!signatureUrl) return toast.error("Capture the signature");
    setBusy(true);
    try {
      const { data } = await api.post("/drop-workflow/bulk-deliver", {
        parcel_ids: Array.from(selected), signed_by: signedBy,
        signature_data_url: signatureUrl, drop_address: dropAddress, remarks,
      });
      toast.success(`${data.delivered} parcel(s) marked delivered`);
      setDlvOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delivery failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-8 space-y-6 max-w-4xl mx-auto" data-testid="runner-view">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Truck size={12}/> Runner · today's list</div>
          <h1 className="text-3xl sm:text-4xl font-display mt-1">My Deliveries.</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Only parcels assigned to you appear here. Tick the ones you dropped at the same address and capture one signature.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="runner-refresh" className="rounded-md">
          <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      <section className="border border-border rounded-md bg-card" data-testid="runner-parcels">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="overline flex items-center gap-1.5"><Package size={11}/> Assigned to me · {parcels.length}</div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground" data-testid="runner-selected-count">{selected.size} selected</span>
            <Button size="sm" onClick={openDlv} disabled={selected.size === 0} data-testid="runner-deliver-btn"
                    className="rounded-md bg-primary text-primary-foreground shadow-brut-orange h-8">
              <PenLine size={12} className="mr-1.5" /> Deliver ({selected.size})
            </Button>
          </div>
        </div>
        {parcels.length === 0 ? (
          <EmptyState icon={Package} title="Nothing assigned to you" testid="runner-empty"
                      subtitle="Ask your executive to assign parcels via Drop Routes." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="p-2 w-10 text-left">
                    <input type="checkbox" data-testid="runner-select-all"
                           checked={allChecked}
                           ref={el => { if (el) el.indeterminate = someChecked; }}
                           onChange={toggleAll} className="h-4 w-4 accent-primary cursor-pointer" />
                  </th>
                  <th className="p-2 text-left">Tracking</th>
                  <th className="p-2 text-left">Recipient</th>
                  <th className="p-2 text-left">Address</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {parcels.map(p => (
                  <tr key={p.id} className={`border-t border-border ${selected.has(p.id) ? "bg-red-50/40" : "hover:bg-muted/30"}`}
                      data-testid={`runner-parcel-${p.tracking_number}`}>
                    <td className="p-2">
                      <input type="checkbox" data-testid={`runner-check-${p.id}`}
                             checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                             className="h-4 w-4 accent-primary cursor-pointer" />
                    </td>
                    <td className="p-2 mono text-xs font-bold">{p.tracking_number}</td>
                    <td className="p-2">
                      {p.recipient_name || "—"}
                      <div className="text-[10px] text-muted-foreground">{p.recipient_department || ""}</div>
                    </td>
                    <td className="p-2 text-[11px] text-muted-foreground truncate max-w-xs">{p.recipient_address || "—"}</td>
                    <td className="p-2 text-[10px] uppercase tracking-widest">{(p.status || "").replace(/_/g," ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Deliver dialog · single signature covers all selected parcels */}
      <Dialog open={dlvOpen} onOpenChange={setDlvOpen}>
        <DialogContent className="rounded-md max-w-lg" data-testid="runner-deliver-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Capture delivery signature</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Marking <b className="text-foreground">{selected.size}</b> parcel(s) as delivered. Same signature applies to all.
            </div>
            <label className="block">
              <span className="overline">Signed by (recipient name) *</span>
              <Input data-testid="runner-signed-by" value={signedBy} onChange={e => setSignedBy(e.target.value)}
                     placeholder="Full name" required className="mt-1 rounded-md" />
            </label>
            <label className="block">
              <span className="overline">Drop address (optional)</span>
              <Input data-testid="runner-drop-address" value={dropAddress} onChange={e => setDropAddress(e.target.value)}
                     placeholder="Building / floor / desk" className="mt-1 rounded-md" />
            </label>
            <div>
              <span className="overline">Signature *</span>
              <div className="mt-1">
                <SignaturePad value={signatureUrl} onChange={setSignatureUrl} testid="runner-signature" height={160} />
              </div>
            </div>
            <label className="block">
              <span className="overline">Remarks (optional)</span>
              <Input data-testid="runner-remarks" value={remarks} onChange={e => setRemarks(e.target.value)}
                     placeholder="Anything to note" className="mt-1 rounded-md" />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDlvOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={confirmDeliver} disabled={busy || !signedBy || !signatureUrl}
                      data-testid="runner-deliver-confirm"
                      className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
                {busy ? "Delivering…" : <>Confirm delivery <CheckCircle2 size={13} className="ml-1.5" /></>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
